import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Shield, Mail, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_LABELS = {
  rent_reminders: "Rent reminders",
  payment_receipts: "Payment receipts",
  maintenance_updates: "Maintenance updates",
  monthly_digests: "Monthly reports & digests",
  marketing: "Product tips & announcements",
  dispute_alerts: "Alerts & critical notices",
};

/**
 * Public unsubscribe landing page.
 *
 * URL: /unsubscribe/:token      → master preferences panel
 *      /unsubscribe/:token?cat=monthly_digests → one-click category opt-out
 *
 * No login required. Renders a masked email so the recipient can confirm
 * they're opting out the right account without leaking PII if the link is
 * forwarded.
 */
export default function Unsubscribe() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const presetCategory = search.get("cat");
  const [state, setState] = useState({ kind: "loading" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/unsubscribe/${token}`);
      setState({ kind: "ok", ...data });
    } catch (e) {
      const code = e?.response?.status;
      if (code === 404) { setState({ kind: "missing" }); return; }
      setState({ kind: "error", message: e?.response?.data?.detail || String(e) });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const handleUnsub = async (body, successMsg) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/unsubscribe/${token}`, body);
      setState((s) => ({ ...s, ...data, kind: "ok", confirmation: successMsg }));
    } catch (e) {
      setState((s) => ({ ...s, error: e?.response?.data?.detail || "Could not update preferences" }));
    } finally { setBusy(false); }
  };

  if (state.kind === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500" data-testid="unsub-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading your preferences…
      </div>
    );
  }
  if (state.kind === "missing") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="unsub-missing">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Link not found</h1>
          <p className="text-sm text-slate-500">
            This unsubscribe link doesn't match any account. If you received this link in an email, it may have expired or been regenerated. Log in to your RealtyVault Manager Settings page to update email preferences directly.
          </p>
        </div>
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 text-sm text-red-600">
        {state.message}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10" data-testid="unsub-page">
      <div className="bg-white rounded-sm border border-slate-200 shadow-sm max-w-lg w-full p-6">
        <div className="flex items-center gap-2 text-slate-700 mb-4">
          <Shield className="w-5 h-5" />
          <span className="overline text-slate-500">Email preferences</span>
        </div>

        <h1 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily:"Outfit"}}>
          Manage emails for <span className="font-mono text-base">{state.email_masked}</span>
        </h1>
        <p className="text-sm text-slate-500 mb-5">
          Choose what you want to stop receiving. Security and password-reset emails are always delivered and can't be paused.
        </p>

        {state.confirmation && (
          <div className="mb-4 rounded-sm border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-2 text-sm text-emerald-800" data-testid="unsub-confirmation">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            <div>{state.confirmation}</div>
          </div>
        )}

        {/* SHORTCUT: preset category from the email link */}
        {presetCategory && CAT_LABELS[presetCategory] && state.email_categories?.[presetCategory] !== false && !state.confirmation && (
          <button
            onClick={() => handleUnsub(
              { category: presetCategory },
              `You're unsubscribed from "${CAT_LABELS[presetCategory]}". You can re-enable it any time below.`,
            )}
            disabled={busy}
            data-testid="unsub-category-quickbutton"
            className="w-full mb-4 rounded-sm border border-slate-300 bg-white hover:bg-slate-50 p-3 text-left group transition"
          >
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-500" />
              <div className="flex-1">
                <div className="font-semibold text-sm text-slate-900">Unsubscribe from "{CAT_LABELS[presetCategory]}"</div>
                <div className="text-xs text-slate-500">One click — we'll stop sending this category to you immediately.</div>
              </div>
              <span className="text-xs font-mono text-slate-900 group-hover:underline">Do it</span>
            </div>
          </button>
        )}

        {/* FULL CATEGORY LIST */}
        <div className="border-t border-slate-100 pt-4">
          <div className="overline text-slate-500 mb-2">All categories</div>
          <ul className="space-y-1.5">
            {(state.available_categories || []).map((cat) => {
              const enabled = state.email_categories?.[cat] !== false;
              return (
                <li key={cat} className="flex items-center justify-between gap-2 py-1" data-testid={`unsub-cat-row-${cat}`}>
                  <div className="text-sm text-slate-900">{CAT_LABELS[cat] || cat}</div>
                  <button
                    onClick={() => handleUnsub(
                      { category: cat },
                      `Unsubscribed from "${CAT_LABELS[cat] || cat}".`,
                    )}
                    disabled={busy || !enabled}
                    data-testid={`unsub-cat-btn-${cat}`}
                    className={`text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm border transition ${
                      enabled
                        ? "border-slate-300 text-slate-700 hover:bg-slate-900 hover:text-white hover:border-slate-900"
                        : "border-emerald-200 bg-emerald-50 text-emerald-700 cursor-default"
                    }`}
                  >
                    {enabled ? "Unsubscribe" : "✓ Unsubscribed"}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* MASTER UNSUB */}
        <div className="mt-5 border-t border-slate-100 pt-4">
          {state.email_opt_in === false ? (
            <div className="rounded-sm bg-slate-100 text-slate-600 text-xs p-3 text-center" data-testid="unsub-all-confirmed">
              All email notifications are currently paused for this account.
              To re-enable emails, log in to your Settings page.
            </div>
          ) : (
            <button
              onClick={() => {
                if (!window.confirm("Unsubscribe from ALL RealtyVault Manager emails? Important account and security messages will still be delivered.")) return;
                handleUnsub(
                  { unsubscribe_all: true },
                  "All commercial emails are now paused for this account.",
                );
              }}
              disabled={busy}
              data-testid="unsub-all-button"
              className="text-xs text-red-600 hover:text-red-800 underline underline-offset-2"
            >
              Unsubscribe from every category instead →
            </button>
          )}
        </div>

        <div className="mt-6 text-[10px] font-mono uppercase tracking-widest text-slate-400 text-center">
          RealtyVault Manager · CAN-SPAM compliant unsubscribe
        </div>
      </div>
    </div>
  );
}
